import Button from "@/components/base/Button";
import PageHero from "@/components/base/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import Accordion, { type FaqItem } from "@/components/base/Accordion";
import { useUi } from "@/context/UiContext";
import Seo from "@/components/base/Seo";
import { faqPageSchema } from "@/lib/seo";

type ActivityStatus = "upcoming" | "closed" | "ended" | "tba";

type Activity = {
  name: string;
  date: string;
  time: string;
  mode: string;
  audience: string;
  fee: string;
  desc: string;
  status: ActivityStatus;
};

const ACTIVITIES: Activity[] = [
  {
    name: "免費講座｜從知道，到做到",
    date: "待公告",
    time: "待公告",
    mode: "線上／實體待定",
    audience: "初次接觸、想了解幸福人才方法的人",
    fee: "免費",
    desc: "一場入門講座，從「知道很多卻做不到」的常見狀態談起，帶你看見自己可能卡在哪裡。",
    status: "tba",
  },
  {
    name: "七日學用覺醒",
    date: "待公告",
    time: "待公告",
    mode: "線上／實體待定",
    audience: "想用短期行動開始改變的人",
    fee: "待公告",
    desc: "七日短期實踐，把一個想改變的目標，拆成每天做得到的小行動。",
    status: "tba",
  },
  {
    name: "幸福人才養成系統",
    date: "待公告",
    time: "待公告",
    mode: "線上／實體待定",
    audience: "想系統性建立成長方式的人",
    fee: "待公告",
    desc: "幸福人才養成系統核心課程，從信念、行動到人生方向，建立能持續運轉的成長方式。",
    status: "tba",
  },
];

const STATUS_MAP: Record<
  ActivityStatus,
  { label: string; variant: "primary" | "outline" | "ghost"; disabled: boolean }
> = {
  upcoming: { label: "立即報名", variant: "primary", disabled: false },
  closed: { label: "報名已截止", variant: "ghost", disabled: true },
  ended: { label: "查看活動回顧", variant: "outline", disabled: false },
  tba: { label: "最新梯次待公告", variant: "ghost", disabled: true },
};

const ENTRY_LEVELS = [
  {
    icon: "ri-gift-line",
    title: "免費開始",
    desc: "講座、文章與公開內容。",
    tag: "認識方法",
  },
  {
    icon: "ri-play-circle-line",
    title: "入門體驗",
    desc: "短期實作／七日行動。",
    tag: "開始行動",
  },
  {
    icon: "ri-flag-2-line",
    title: "完整學習",
    desc: "幸福人才養成系統。",
    tag: "系統建立",
  },
  {
    icon: "ri-rocket-2-line",
    title: "進階成長",
    desc: "陪跑／進階學習。",
    tag: "深度成長",
  },
];

const PLANS = [
  {
    name: "免費講座｜從知道，到做到",
    type: "免費開始",
    desc: "入門講座，認識幸福人才方法。",
  },
  {
    name: "七日學用覺醒",
    type: "入門體驗",
    desc: "七日短期實踐，把改變變成行動。",
  },
  {
    name: "幸福人才養成系統",
    type: "完整學習",
    desc: "核心課程，建立能持續的成長方式。",
  },
  {
    name: "進階方案",
    type: "進階成長",
    desc: "陪跑與進階學習。",
  },
];

const FAQ: FaqItem[] = [
  {
    q: "第一次接觸，需要直接買課嗎？",
    a: "不需要。你可以先從免費講座、公開內容開始，或用 30 秒快篩、和 AI 顧問聊聊，先了解自己目前的位置，再決定適合的起點。",
  },
  {
    q: "所有人都從同一個方案開始嗎？",
    a: "不是。每個人的狀態不同，可以依目前卡點選擇免費開始、入門體驗或完整學習等不同的起點。",
  },
  {
    q: "課程是否保證成果？",
    a: "幸福人才強調的是建立屬於自己的方法，而非保證特定成果。改變需要時間與行動，課程提供方法與系統，成果仍取決於持續的實踐。",
  },
  {
    q: "不知道怎麼選課怎麼辦？",
    a: "可以先做 30 秒行動卡點快篩，或和 AI 幸福人才顧問聊聊，先整理自己目前卡在哪裡，我們也會協助你找到適合的起點。",
  },
];

export default function Courses() {
  const { openQuiz, openAi } = useUi();

  return (
    <>
      <Seo
        title="課程與活動｜幸福人才養成系統・講座・工作坊｜許文彰"
        description="幸福人才課程與活動包含免費講座、七日學用覺醒、幸福人才養成系統等方案，依你目前的狀態選擇適合的起點。"
        keywords="課程,活動,講座,工作坊,幸福人才養成系統,許文彰"
        jsonLd={faqPageSchema(FAQ)}
      />
      <PageHero
        eyebrow="課程與活動"
        title={
          <>
            不需要一次改變全部，
            <br />
            先找到適合你的起點。
          </>
        }
        description={
          <>
            <p>
              每個人的狀態不同。有人正在找方向。有人已經知道問題，卻一直沒有開始。
              有人真正需要的是如何持續。你可以依現在的狀態，選擇適合自己的開始方式。
            </p>
          </>
        }
      >
        <Button
          onClick={() =>
            document.getElementById("activities")?.scrollIntoView({ behavior: "smooth" })
          }
          icon="ri-arrow-down-line"
        >
          查看近期活動
        </Button>
      </PageHero>

      {/* 近期活動 */}
      <section id="activities" className="bg-white scroll-mt-20">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="近期活動" title="最近，可以從這裡開始。" />
            <p className="mt-4 text-sm text-ink-soft/70">
              近期活動梯次與日期待公告，敬請期待。
            </p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ACTIVITIES.map((a, i) => {
              const s = STATUS_MAP[a.status];
              return (
                <Reveal key={a.name} delay={i * 90}>
                  <div className="h-full flex flex-col rounded-card bg-cream border border-line p-7 transition-transform duration-300 hover:-translate-y-1">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex rounded-full bg-gold-soft/60 px-3 py-1 text-xs font-semibold text-primary">
                        {a.mode}
                      </span>
                      <span className="text-xs text-ink-soft/60">{a.fee}</span>
                    </div>
                    <h3 className="mt-4 text-lg font-bold text-ink">{a.name}</h3>
                    <p className="mt-3 text-sm leading-[1.8] text-ink-soft">{a.desc}</p>

                    <dl className="mt-5 space-y-2 text-sm text-ink-soft">
                      <div className="flex items-start gap-2">
                        <i className="ri-calendar-line text-gold mt-0.5" aria-hidden="true"></i>
                        <span>{a.date} · {a.time}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <i className="ri-user-line text-gold mt-0.5" aria-hidden="true"></i>
                        <span>{a.audience}</span>
                      </div>
                    </dl>

                    <div className="mt-6 pt-5 border-t border-line mt-auto">
                      {s.disabled ? (
                        <span className="inline-flex items-center justify-center rounded-md bg-line/60 px-5 py-2.5 text-sm font-semibold text-ink-soft/60 cursor-not-allowed">
                          {s.label}
                        </span>
                      ) : (
                        <Button variant={s.variant} className="w-full">
                          {s.label}
                        </Button>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 四種學習入口 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="學習入口" title="從目前的狀態開始。" align="center" />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ENTRY_LEVELS.map((e, i) => (
              <Reveal key={e.title} delay={i * 80}>
                <div className="h-full rounded-card bg-white border border-line p-7 transition-transform duration-300 hover:-translate-y-1">
                  <span className="inline-flex rounded-full bg-cream border border-line px-3 py-1 text-xs font-medium text-ink-soft">
                    {e.tag}
                  </span>
                  <div className="mt-5 w-12 h-12 flex items-center justify-center rounded-md bg-gold-soft/70">
                    <i className={`${e.icon} text-2xl text-primary`} aria-hidden="true"></i>
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-ink">{e.title}</h3>
                  <p className="mt-2 text-sm leading-[1.8] text-ink-soft">{e.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 目前方案 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="目前方案" title="從一個適合你的起點開始。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i * 80}>
                <div className="h-full flex flex-col rounded-card bg-cream border border-line p-7">
                  <p className="text-xs font-semibold text-gold uppercase tracking-wide">
                    {p.type}
                  </p>
                  <h3 className="mt-3 text-base font-bold text-ink leading-[1.5]">{p.name}</h3>
                  <p className="mt-3 text-sm leading-[1.8] text-ink-soft">{p.desc}</p>
                  <div className="mt-6 pt-5 border-t border-line mt-auto">
                    <span className="inline-flex items-center gap-2 text-xs font-medium text-ink-soft/70">
                      <i className="ri-time-line" aria-hidden="true"></i>
                      最新梯次待公告
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 不知道怎麼選 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <SectionHeading
                eyebrow="不知道怎麼選"
                title="不用先從價格判斷，從現在的問題開始。"
                align="center"
              />
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={openQuiz} icon="ri-arrow-right-line">
                  30 秒找出我的行動卡點
                </Button>
                <Button onClick={openAi} variant="outline">
                  和 AI 幸福人才顧問聊聊
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 學員回饋 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="學員回饋" title="聽聽走過的人，怎麼說。" align="center" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="h-full rounded-card bg-cream border border-line p-7">
                  <span className="inline-flex items-center rounded-full bg-gold-soft/60 px-3 py-1 text-xs font-medium text-primary">
                    回饋待補
                  </span>
                  <div className="mt-5 flex items-center gap-1 text-gold">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <i key={s} className="ri-star-fill text-sm" aria-hidden="true"></i>
                    ))}
                  </div>
                  <p className="mt-4 text-sm text-ink-soft/50">真實學員回饋內容建置中</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={140}>
            <p className="mt-8 text-center text-xs text-ink-soft/60">
              僅採用真實學員回饋，內容待業主提供後補上
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="常見問題" title="關於課程與活動，你可能想知道。" />
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-8 max-w-3xl">
              <Accordion items={FAQ} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}